import { useEffect, useState } from 'react';
import {
  levelLabel,
  nextLevel,
  type Origin,
  type PermissionLevel,
  type PermissionPolicy,
} from '@elena/shared';
import { api, type ActionMeta, type PermissionsResponse } from '../lib/api';

const columns: Array<{ origin: Origin; label: string }> = [
  { origin: 'pc', label: 'Sa računara' },
  { origin: 'phone', label: 'Sa telefona' },
  { origin: 'web', label: 'Sa weba' },
];

export function Permissions() {
  const [data, setData] = useState<PermissionsResponse | null>(null);
  const [meta, setMeta] = useState<Record<string, ActionMeta>>({});
  const [policy, setPolicy] = useState<PermissionPolicy>({});
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    void Promise.all([api.permissions(), api.actions()]).then(([p, a]) => {
      setData(p);
      setPolicy(p.policy);
      setMeta(Object.fromEntries(a.actions.map((x) => [x.id, x])));
    });
  }, []);

  if (!data)
    return (
      <div className="page">
        <p className="muted">Učitavam dozvole…</p>
      </div>
    );

  const levelFor = (action: string, origin: Origin): PermissionLevel =>
    (policy as Record<string, Partial<Record<Origin, PermissionLevel>>>)[action]?.[origin] ??
    data.matrix.find((r) => r.action === action)?.levels[origin] ??
    'deny';

  const cycle = (action: string, origin: Origin) => {
    const next = nextLevel(levelFor(action, origin));
    setPolicy((p) => {
      const current = (p as Record<string, Partial<Record<Origin, PermissionLevel>>>)[action];
      return { ...p, [action]: { ...current, [origin]: next } } as PermissionPolicy;
    });
    setState('idle');
  };

  const save = async () => {
    setState('saving');
    try {
      const res = await api.savePermissions(policy);
      setData(res);
      setPolicy(res.policy);
      setState('saved');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Dozvole</h1>
          <p className="muted">
            Šta Elena sme, sa kog uređaja, i kada mora prvo da te pita. Klikni oznaku da je
            promeniš.
          </p>
        </div>
        <button
          type="button"
          className="btn primary"
          onClick={() => void save()}
          disabled={state === 'saving'}
        >
          {state === 'saving' ? 'Čuvam…' : state === 'saved' ? 'Sačuvano' : 'Sačuvaj'}
        </button>
      </header>
      {state === 'error' && (
        <p className="notice">Nisam uspela da sačuvam. Proveri vezu i pokušaj ponovo.</p>
      )}

      <section className="card">
        <div className="table-wrap">
          <table className="table perm-table">
            <thead>
              <tr>
                <th scope="col">Akcija</th>
                {columns.map((c) => (
                  <th key={c.origin} scope="col">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.matrix.map((row) => (
                <tr key={row.action}>
                  <th scope="row" className="perm-action">
                    <span className="strong">{meta[row.action]?.description ?? row.action}</span>
                    <span className={`risk risk-${meta[row.action]?.risk ?? 'low'}`}>
                      {meta[row.action]?.risk === 'high'
                        ? 'opasno'
                        : meta[row.action]?.risk === 'medium'
                          ? 'pažljivo'
                          : 'bezbedno'}
                    </span>
                  </th>
                  {columns.map((c) => {
                    const level = levelFor(row.action, c.origin);
                    return (
                      <td key={c.origin} data-label={c.label}>
                        <button
                          type="button"
                          className={`level level-${level}`}
                          onClick={() => cycle(row.action, c.origin)}
                          aria-label={`${meta[row.action]?.description ?? row.action}, ${c.label.toLowerCase()}: ${levelLabel[level]}. Klikni za promenu.`}
                        >
                          {levelLabel[level]}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <p className="muted small">
        Desktop agent ima i svoju listu (ELENA_ACTIONS). Akcija radi samo ako je dozvoljena i ovde i
        tamo.
      </p>
    </div>
  );
}
